import { ConfirmModal, Loading } from '@/components';
import { useExpenditureBudgetPurpose } from '@/features/master-table/hooks';
import { usePolicies } from '@/features/master-table/policy/_hooks/policy';
import { useCheckRemoveNotExistUserIdOrNotExistOrganizationId } from '@/features/project/_hooks/form-control';
import { useProject, useSplitProject } from '@/features/project/_hooks/project';
import type {
  KsFormSchema,
  ProjectFormSchema,
  RsFormSchema,
  SsFormSchema,
} from '@/features/project/projects/_models/project';
import { ProjectForm } from '@/features/project/projects/upsert-form/_components/ProjectForm';
import { getInitialAdditionalJurisdictionalUsersData } from '@/features/project/review-sheet/utils/format-project-data';
import {
  useOrganizationRecursive,
  useRecursiveOrganizationFilteredByKey,
} from '@/features/tenant-manager/hooks';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { findUpperRecursive } from '@/utils';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import invariant from 'tiny-invariant';
import type { z } from 'zod';

export const SplitProjectPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openMessage } = useMessage();
  const { id } = useId();
  const project = useProject(id);
  const {
    openUpdateConfirmModal,
    onCancelModal,
    checkRemoveNotExistUserIdOrNotExistOrganizationId,
  } = useCheckRemoveNotExistUserIdOrNotExistOrganizationId();

  const { mutateAsync } = useSplitProject();

  const { data: organizationIds } = useRecursiveOrganizationFilteredByKey({
    params: { ids: [project.data?.organizationId ?? ''] },
    enabled: !!project.data?.organizationId,
    options: { separator: undefined, filterBy: 'id', reverse: true },
    select: (data) => data.flat(),
  });

  const relatedOrganizationIds = useRecursiveOrganizationFilteredByKey({
    params: { ids: project.data?.relatedOrganizationIds ?? [] },
    enabled: !!project.data?.relatedOrganizationIds.length,
    options: { separator: undefined, filterBy: 'id', reverse: true },
  });

  const nestedOtherResponsibleOrganizationIds =
    useRecursiveOrganizationFilteredByKey({
      params: { ids: project.data?.otherResponsibleOrganizationIds ?? [] },
      enabled: !!project.data?.otherResponsibleOrganizationIds.length,
      options: { separator: undefined, filterBy: 'id', reverse: true },
    });

  const additionalJurisdictionalOrganizationIds =
    useRecursiveOrganizationFilteredByKey({
      params: {
        ids:
          project.data?.fundAdditionalAttributes?.additionalJurisdictionalUsers?.map(
            (v) => v.organizationId,
          ) ?? [],
      },
      enabled:
        project.data?.sheetType === 'KS' &&
        !!project.data.fundAdditionalAttributes?.additionalJurisdictionalUsers
          ?.length,
      options: { separator: undefined, filterBy: 'id', reverse: true },
    });

  const policies = usePolicies();
  const { data: independentAdministrativeAgencyIds } = useOrganizationRecursive(
    {
      id:
        project.data?.sheetType === 'SS'
          ? (project.data.independentAdministrativeAgencyId ?? '')
          : '',
      select: (data) => data.map((d) => d.id),
    },
  );

  const expenditureBudgetPurpose = useExpenditureBudgetPurpose({
    id: project.data?.majorExpenditureBudgetPurposeId ?? '',
  });

  if (
    !project.data ||
    !organizationIds ||
    (project.data.sheetType === 'RS' && !policies.data) ||
    (project.data.sheetType === 'SS' && !independentAdministrativeAgencyIds) ||
    (project.data.skipAdministrativeReformTeamEvaluation &&
      !expenditureBudgetPurpose.data)
  ) {
    return <Loading />;
  }

  const initialFundAdditionalAttributes = project.data.fundAdditionalAttributes
    ? {
        ...project.data.fundAdditionalAttributes,
        additionalJurisdictionalUsers:
          getInitialAdditionalJurisdictionalUsersData(
            additionalJurisdictionalOrganizationIds.data ?? [],
            project.data.fundAdditionalAttributes.additionalJurisdictionalUsers,
          ),
      }
    : null;

  const onSplitProject = async (values: z.infer<typeof ProjectFormSchema>) => {
    const { newSplitProjectId } = await mutateAsync({ id, ...values });
    onCancelModal();
    await Promise.all([
      openMessage('success', t('message-update-completed')),
      (async () => {
        navigate(`/rs/project/${newSplitProjectId}`);
      })(),
    ]);
  };

  return (
    <>
      <ProjectForm
        type="edit"
        isSubmitEnabledOnMount
        openUpdateConfirmModal={openUpdateConfirmModal.isOpen}
        initialValues={
          project.data.sheetType === 'RS'
            ? {
                sheetType: 'RS',
                data: {
                  // FIXME: zod parseするか、propertyを一個ずつ渡して型エラーをなくしてください
                  ...(project.data as unknown as z.infer<
                    typeof RsFormSchema
                  >['data']),
                  skipAdministrativeReformTeamEvaluation:
                    project.data.skipAdministrativeReformTeamEvaluation,
                  fiscalYear: dayjs().year(project.data.fiscalYear),
                  period: [
                    dayjs().year(project.data.fiscalYear),
                    project.data.endYear
                      ? dayjs().year(project.data.endYear)
                      : undefined,
                  ],
                  organizationIds,
                  nestedRelatedOrganizationIds: relatedOrganizationIds.data,
                  policiesForProject: project.data.policiesForProject.map(
                    (policyForProject) => {
                      const currentPolicies = findUpperRecursive(
                        policyForProject.policyId,
                        policies.data ?? [],
                      ).reverse();
                      return {
                        policyId: currentPolicies.map((d) => d.id),
                        policyUrl: policyForProject.policyUrl,
                      };
                    },
                  ),
                  mainCostIds: project.data.mainCosts.map(
                    (mainCost) => mainCost.id,
                  ),
                  majorExpenditureBudgetPurposeIds: [
                    expenditureBudgetPurpose.data?.accountingId,
                    expenditureBudgetPurpose.data?.jurisdictionId,
                    expenditureBudgetPurpose.data?.accountingType.type ===
                    'general'
                      ? expenditureBudgetPurpose.data.organizationId
                      : expenditureBudgetPurpose.data?.accountTitleId,
                    expenditureBudgetPurpose.data?.expenditurePurpose.upperId,
                    expenditureBudgetPurpose.data?.id,
                  ].flatMap((v) => v ?? []),
                  relatedProjects: [],
                  nestedOtherResponsibleOrganizationIds:
                    nestedOtherResponsibleOrganizationIds.data,
                },
              }
            : project.data.sheetType === 'SS'
              ? {
                  sheetType: 'SS',
                  data: {
                    // FIXME: zod parseするか、propertyを一個ずつ渡して型エラーをなくしてください
                    ...(project.data as unknown as z.infer<
                      typeof SsFormSchema
                    >['data']),
                    fiscalYear: dayjs().year(project.data.fiscalYear),
                    period: [
                      dayjs().year(project.data.fiscalYear),
                      project.data.endYear
                        ? dayjs().year(project.data.endYear)
                        : undefined,
                    ],
                    organizationIds,
                    nestedRelatedOrganizationIds: relatedOrganizationIds.data,
                    independentAdministrativeAgencyIds,
                    mainCostIds: project.data.mainCosts.map(
                      (mainCost) => mainCost.id,
                    ),
                    relatedProjects: [],
                    nestedOtherResponsibleOrganizationIds:
                      nestedOtherResponsibleOrganizationIds.data,
                  },
                }
              : {
                  sheetType: 'KS',
                  data: {
                    // FIXME: zod parseするか、propertyを一個ずつ渡して型エラーをなくしてください
                    ...(project.data as unknown as z.infer<
                      typeof KsFormSchema
                    >['data']),
                    fundAdditionalAttributes: initialFundAdditionalAttributes
                      ? {
                          ...initialFundAdditionalAttributes,
                          scheduledEnd: {
                            ...initialFundAdditionalAttributes?.scheduledEnd,
                            endDate: initialFundAdditionalAttributes
                              ?.scheduledEnd?.endDate
                              ? dayjs(
                                  initialFundAdditionalAttributes.scheduledEnd
                                    .endDate,
                                )
                              : undefined,
                            endDateOfNewApplicationReceiving:
                              initialFundAdditionalAttributes?.scheduledEnd
                                ?.endDateOfNewApplicationReceiving
                                ? dayjs(
                                    initialFundAdditionalAttributes.scheduledEnd
                                      .endDateOfNewApplicationReceiving,
                                  )
                                : undefined,
                          },
                        }
                      : null,
                    fiscalYear: dayjs().year(project.data.fiscalYear),
                    period: [
                      dayjs().year(project.data.fiscalYear),
                      project.data.endYear
                        ? dayjs().year(project.data.endYear)
                        : undefined,
                    ],
                    organizationIds,
                    nestedRelatedOrganizationIds: relatedOrganizationIds.data,
                    relatedProjects: [],
                    nestedOtherResponsibleOrganizationIds:
                      nestedOtherResponsibleOrganizationIds.data,
                  },
                }
        }
        onSubmit={async (values) => {
          const { isRemovedNotExistValue } =
            await checkRemoveNotExistUserIdOrNotExistOrganizationId(values);
          if (!isRemovedNotExistValue) {
            await onSplitProject(values);
          }
        }}
      />
      <ConfirmModal
        okText={t('save')}
        open={openUpdateConfirmModal.isOpen}
        title={t('save-confirmation')}
        disclaimerText={t('change-not-exist-in-value-confirmation')}
        onOk={async () => {
          invariant(
            openUpdateConfirmModal.isOpen,
            'The modal is submit with the modal closed.',
          );
          await onSplitProject(openUpdateConfirmModal.values);
        }}
        onCancel={onCancelModal}
        skipCheck
      />
    </>
  );
};
